<script setup>
import LoadingAnimation from "@Components/Animation/Loading";
import { PrimaryButton } from "@Components/Button";
import Step from "@Components/Stepper/Step.vue";
import { MagicIcon } from "@Icons/index";
import { useI18n } from "vue-i18n";
import _groupBy from "lodash/groupBy";
import _map from "lodash/map";
import _filter from "lodash/filter";
import _cloneDeep from "lodash/cloneDeep";
import { models as imagesModels, getProvider as getImageProvider } from "@Utils/image-models";
import { FormGroupListbox, FormTextarea } from "@Components/Form";

const props = defineProps(["modelValue", "step"]);
defineEmits(["update:modelValue", "next", "back", "skip"]);

const { t: __ } = useI18n();

const generate = async () => {
    if (props.modelValue.processing) return;

    const response = await props.modelValue.post("personas_generate_avatar");

    if (response.avatar !== undefined) {
        props.modelValue.avatar = response.avatar;
    }
};

const imagesModelsGroups = _map(_groupBy(_filter(_cloneDeep(imagesModels), "is_ai"), "provider"), (items, provider) => {
    return {
        name: getImageProvider(provider).name,
        items: items,
    };
});
</script>

<template>
    <Step :step="step" @next="$emit('next')" @back="$emit('back')" @skip="$emit('skip')">
        <template #header>
            <div class="mx-auto max-w-3xl">
                <h3 class="text-4xl font-semibold leading-tight text-center text-gray-900 dark:text-white">
                    {{ __("Générez un avatar") }}
                </h3>
            </div>
        </template>
        <div class="mx-auto max-w-4xl flex flex-col space-y-6">
            <div
                class="w-[512px] mx-auto p-6 text-gray-600 bg-gray-50 rounded-lg dark:text-gray-400 dark:bg-gray-900"
                v-html="
                    __('Cette fonctionnalité nécessite le plugin Simple Local Avatars. Il est disponible sur le {startlink}store WordPress{endlink}.', {
                        startlink:
                            '<a href=\'https://wordpress.org/plugins/simple-local-avatars/\' target=\'_blank\' class=\'font-bold text-gray-900 dark:text-white hover:underline\'>',
                        endlink: '</a>',
                    })
                "></div>
            <div class="mx-auto relative flex">
                <div :class="['p-2 bg-gray-900 rounded-lg', modelValue.processing ? 'animate-pulse' : '']">
                    <img
                        v-if="modelValue.avatar"
                        :src="modelValue.avatar.url"
                        class="max-h-[512px] max-w-[512px] aspect-square w-full h-full object-cover bg-gray-800 rounded-lg" />
                    <div v-else class="h-[512px] w-[512px] aspect-square bg-gray-50 rounded-lg dark:bg-gray-900"></div>
                </div>
                <div v-if="modelValue.processing" class="bg-opacity-60 absolute inset-0 flex justify-center items-center bg-white dark:bg-gray-700">
                    <LoadingAnimation class="w-16 h-16" />
                </div>
            </div>
            <div class="mx-auto w-full max-w-sm">
                <FormGroupListbox v-model="modelValue.avatar_model" :groups="imagesModelsGroups" required />
            </div>
            <div class="mx-auto w-full max-w-lg">
                <FormTextarea
                    :label="__('Instructions personnalisées')"
                    rows="5"
                    v-model="modelValue.avatar_custom_instructions"
                    :errors="modelValue.errors.get('avatar_custom_instructions')" />
            </div>
            <div class="flex justify-center">
                <PrimaryButton @click="generate" class="gap-x-1.5" :icon="MagicIcon">
                    {{ __("Générer un avatar avec l'IA") }}
                </PrimaryButton>
            </div>
        </div>
    </Step>
</template>
